import Styles from './styles.module.css';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { getAuth } from 'firebase/auth';
import { doc, getFirestore, setDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes } from 'firebase/storage';
import { useAppSelector } from 'src/salon/app/hook';
import { StaffOption } from '../types';
import { GENDER_VALUES } from '../tagConstants';
import { DraftImage } from './types';
import UploadPhotoEditor from './2.editor/index';
import ConfirmDialog from '../confirm/index';
import { IoClose } from 'react-icons/io5';

type PhotoUploadDialogProps = {
  staffList: StaffOption[];
  onClose: () => void;
};

function isComplete(image: DraftImage): boolean {
  return image.tags.some((t) => GENDER_VALUES.includes(t));
}

const PhotoUploadDialog: React.FC<PhotoUploadDialogProps> = ({ staffList, onClose }) => {

  const auth = getAuth();
  const user = auth.currentUser;
  const company = useAppSelector((state) => state.company.company);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [ images, setImages ] = useState<DraftImage[]>([]);
  const [ activeIndex, setActiveIndex ] = useState(0);
  const [ pendingTags, setPendingTags ] = useState<string[]>([]);
  const [ uploading, setUploading ] = useState(false);
  const [ submitAttempted, setSubmitAttempted ] = useState(false);
  const [ confirmClose, setConfirmClose ] = useState(false);

  const activeImage = images[activeIndex] ?? null;

  // 画像未選択の「＋画像を追加」画面は挟まず、開いたらすぐOSのファイル選択を出す
  useEffect(() => {
    if (images.length === 0) fileInputRef.current?.click();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const invalidLocalIds = useMemo(
    () => (submitAttempted ? images.filter((img) => !isComplete(img)).map((img) => img.localId) : []),
    [submitAttempted, images]
  );

  // 作品撮りの投稿は単一投稿のみのため、選択のたびに既存の画像を置き換える。
  function handleSelectFiles(e: React.ChangeEvent<HTMLInputElement>) {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];

    images.forEach((img) => URL.revokeObjectURL(img.previewUrl));

    const newImage: DraftImage = {
      localId: uuid(),
      file,
      previewUrl: URL.createObjectURL(file),
      title: '',
      description: '',
      tags: pendingTags,
      staffId: '',
    };
    setImages([newImage]);
    setActiveIndex(0);
    e.target.value = '';
  }

  function changeTags(tags: string[]) {
    if (activeImage) {
      setImages((prev) => prev.map((img, i) => i === activeIndex ? { ...img, tags } : img));
    } else {
      setPendingTags(tags);
    }
  }

  function handleRequestClose() {
    if (images.length > 0 && !uploading) {
      setConfirmClose(true);
    } else if (!uploading) {
      onClose();
    }
  }

  async function submitAll() {
    if (!user || !activeImage) return;

    setSubmitAttempted(true);
    const incompleteIndex = images.findIndex((img) => !isComplete(img));
    if (incompleteIndex !== -1) {
      setActiveIndex(incompleteIndex);
      return;
    }

    setUploading(true);
    const db = getFirestore();
    const storage = getStorage();

    try {
      for (const image of images) {
        await uploadBytes(ref(storage, `image/${image.localId}`), image.file);

        await setDoc(doc(db, 'posts', image.localId), {
          companyId: company.id,
          post_count: 0,
          post_liker: [],
          post_likes: [],
          post_instagram: '',
          post_tiktok: '',
          post_user_image_1080: '',
          post_user_image_500: '',
          post_name: company.name,
          post_image_1080: '',
          post_image_500: '',
          post_image_name: '',
          post_image_path: '',
          post_tags: image.tags,
          post_uid: user.uid,
          post_time: new Date(),
          post_views: [],
          post_student: false,
          post_title: image.title,
          post_description: image.description,
          post_staff_id: image.staffId,
          post_status: '公開中',
        });
      }
      onClose();
    } catch (error) {
      console.error('upload failed:', error);
    } finally {
      setUploading(false);
    }
  }

  const isWindows = navigator.userAgent.includes("Windows");
  const modalZoom = isWindows ? 1 / 0.8 : 1;

  return (
    <>
    <div className={Styles.overlay} role='dialog' aria-modal='true' style={{ zoom: modalZoom }}>
      <div className={Styles.scrim} onClick={handleRequestClose} />

      <div className={Styles.card}>

        <div className={Styles.header}>
          <h3 className={Styles.headerTitle}>画像をアップロード</h3>
          <IoClose className={Styles.closeIcon} onClick={handleRequestClose} />
        </div>

        <div className={Styles.body}>
          <button
            type='button'
            className={Styles.changeButton}
            onClick={() => fileInputRef.current?.click()}
          >
            {activeImage ? '画像を変更' : '画像を選択'}
          </button>
          <input
            ref={fileInputRef}
            type='file'
            accept='image/*'
            onChange={handleSelectFiles}
            className={Styles.hiddenInput}
          />

          <UploadPhotoEditor
            previewUrl={activeImage?.previewUrl}
            tags={activeImage ? activeImage.tags : pendingTags}
            onChangeTags={changeTags}
            onPick={() => fileInputRef.current?.click()}
            error={activeImage && invalidLocalIds.includes(activeImage.localId) ? 'スタイルを選択してください' : undefined}
          />
        </div>

        <div className={Styles.footer}>
          <button type='button' className={Styles.cancelButton} onClick={handleRequestClose}>キャンセル</button>
          <button type='button' className={Styles.submitButton} onClick={submitAll} disabled={uploading || !activeImage}>
            {uploading ? '投稿中…' : '投稿'}
          </button>
        </div>

      </div>
    </div>

    {confirmClose && (
      <ConfirmDialog
        title="アップロードを中止しますか？"
        desc={"選択した画像の設定内容は破棄されます。\n閉じてよろしいですか？"}
        confirmLabel="破棄して閉じる"
        danger
        onConfirm={() => { setConfirmClose(false); onClose(); }}
        onCancel={() => setConfirmClose(false)}
      />
    )}
    </>
  );
}

export default PhotoUploadDialog;
